'use client'

import {
  arrayMove,
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  SortableContext,
  sortableKeyboardCoordinates,
  TouchSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
  verticalListSortingStrategy,
} from '@repo/ui/dnd'
import type { ReactNode } from 'react'

/** Keeps each sortable list's drag interaction and accessibility in its own context. */
export const SortableList = <Id extends UniqueIdentifier>({
  children,
  disabled,
  ids,
  labels,
  onOrderChange,
}: {
  children: ReactNode
  disabled: boolean
  ids: readonly Id[]
  labels: Record<Id, string>
  onOrderChange: (_order: Id[]) => void
}) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const indexOf = (id: UniqueIdentifier) => ids.findIndex(candidate => candidate === id)
  const labelOf = (id: UniqueIdentifier) => {
    const known = ids.find(candidate => candidate === id)
    return known === undefined ? 'item' : labels[known]
  }
  const positionOf = (id: UniqueIdentifier) => `position ${indexOf(id) + 1} of ${ids.length}`

  return (
    <DndContext
      accessibility={{
        announcements: {
          onDragCancel: ({ active }) => `${labelOf(active.id)} was not moved.`,
          onDragEnd: ({ active, over }) =>
            over ? `${labelOf(active.id)} moved to ${positionOf(over.id)}.` : undefined,
          onDragOver: ({ active, over }) =>
            over ? `${labelOf(active.id)} is at ${positionOf(over.id)}.` : undefined,
          onDragStart: ({ active }) => `Moving ${labelOf(active.id)}.`,
        },
        screenReaderInstructions: {
          draggable:
            'Use space to pick up an item, arrow keys to move it, space to drop it, and Escape to cancel.',
        },
      }}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (disabled || !over) return
        const from = indexOf(active.id)
        const to = indexOf(over.id)
        if (from < 0 || to < 0 || from === to) return
        onOrderChange(arrayMove([...ids], from, to))
      }}
      sensors={sensors}
    data-sentry-element="DndContext" data-sentry-component="SortableList" data-sentry-source-file="sortable-list.tsx">
      <SortableContext items={[...ids]} strategy={verticalListSortingStrategy} data-sentry-element="SortableContext" data-sentry-source-file="sortable-list.tsx">
        {children}
      </SortableContext>
    </DndContext>
  )
}
